import React from 'react';

import classNames from 'classnames';
import { format } from 'date-fns';

import { formatFigure, formatMoney, ZERO_DASH } from 'Helpers/formatService';

/**
 * IB-757 — Budget Versions v2, Phase C (compare polish).
 *
 * Side-by-side comparison of two selected versions, laid out to the agreed v2
 * prototype: a header band ("Comparing" + the variance direction), the two
 * version columns — REFERENCE (the lower version, shaded) and COMPARISON (the
 * higher) — and a Movement column, followed by "Financial headlines" and "Shape
 * of budget" sections. Each line is metric · reference value · subject value ·
 * movement (■/▲/▼ + value + %).
 *
 * The variance is coloured by SIGN (positive green / negative red / neutral for
 * zero), NOT semantically — the agreed compare-view behaviour. Financial figures
 * use formatMoney; shape counts render as plain integers.
 */

const GRID_COLS = 'tw-grid tw-grid-cols-[1.6fr_1fr_1fr_1fr] tw-min-w-[640px]';

/**
 * A signed, one-decimal percentage, e.g. "(+7.0%)" / "(-12.3%)"; "–" for zero and
 * "(+<0.1%)" for a move that rounds to zero. Null when there is nothing
 * meaningful to show — the backend guards a zero reference with a null
 * variance_pct.
 */
function formatPercentage(pct) {
    if (typeof pct !== 'number' || !isFinite(pct)) {
        return null;
    }

    const magnitude = formatFigure(Math.abs(pct), (value, precision) => value.toFixed(precision), { precision: 1 });

    return magnitude === ZERO_DASH ? ZERO_DASH : `(${pct > 0 ? '+' : '-'}${magnitude}%)`;
}

// formatMoney shows up to three decimals; shape counts are whole numbers.
const formatMoneyFigure = (value) => formatFigure(value, formatMoney, { precision: 3 });
const formatCount = (value) => formatFigure(value, String);

function formatDate(value) {
    return value ? format(new Date(value), 'dd-MM-yyyy') : '';
}

function ColumnHead({ label, name, version, date, shaded }) {
    return (
        <div className={classNames('tw-px-4 tw-py-4', shaded && 'tw-bg-background')}>
            <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{label}</div>
            <div className="tw-mt-1 tw-text-base tw-font-bold tw-text-charcoal">{name}</div>
            <div className="tw-mt-0.5 tw-text-2xs tw-text-charcoal-50">
                {$t('(v{{version}})', { version })} · {formatDate(date)}
            </div>
        </div>
    );
}

function ComparisonLine({ row, formatValue }) {
    const variance = row.variance;
    const varianceText = formatValue(Math.abs(variance));
    const isNeutral = varianceText === ZERO_DASH;
    const arrow = isNeutral ? '■' : variance > 0 ? '▲' : '▼';
    const colourClass = isNeutral ? 'tw-text-charcoal-50' : variance > 0 ? 'tw-text-green' : 'tw-text-red';
    const pctLabel = formatPercentage(row.variance_pct);

    return (
        <div className={classNames(GRID_COLS, 'tw-border-b tw-border-grey hover:tw-bg-blue-10')}>
            <div className="tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-charcoal-75">{row.label}</div>
            <div className="tw-bg-background tw-px-4 tw-py-3 tw-text-end tw-text-sm tw-font-semibold tw-text-charcoal tw-tabular-nums">
                {formatValue(row.reference)}
            </div>
            <div className="tw-px-4 tw-py-3 tw-text-end tw-text-sm tw-font-semibold tw-text-charcoal tw-tabular-nums">{formatValue(row.subject)}</div>
            <div
                className={classNames(
                    'tw-flex tw-items-baseline tw-justify-end tw-gap-x-1 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-tabular-nums',
                    colourClass,
                )}
            >
                <span>{arrow}</span>
                <span>{varianceText}</span>
                {pctLabel && <span className="tw-text-xs tw-font-normal tw-opacity-80">{pctLabel}</span>}
            </div>
        </div>
    );
}

function ComparisonSection({ title, rows, formatValue }) {
    return (
        <>
            <div className="tw-min-w-[640px] tw-border-b tw-border-grey tw-bg-background tw-px-4 tw-py-2 tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                {title}
            </div>
            {rows.map((row) => (
                <ComparisonLine key={row.key} row={row} formatValue={formatValue} />
            ))}
        </>
    );
}

export default function CompareVersions({ comparison, onBack }) {
    if (!comparison) {
        return null;
    }

    const { reference, subject, financial, shape } = comparison;

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            <div className="tw-rounded-lg tw-border-2 tw-border-grey tw-overflow-x-auto">
                {/* Header band */}
                <div className={classNames(GRID_COLS, 'tw-border-b tw-border-grey')}>
                    <div className="tw-px-4 tw-py-4">
                        <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{$t('Comparing')}</div>
                        <div className="tw-mt-2 tw-text-xs tw-text-charcoal-75">
                            {$t('Variance = v{{subject}} minus v{{reference}}', { subject: subject.version, reference: reference.version })}
                        </div>
                    </div>
                    <ColumnHead label={$t('Reference')} name={reference.name} version={reference.version} date={reference.date} shaded />
                    <ColumnHead label={$t('Comparison')} name={subject.name} version={subject.version} date={subject.date} />
                    <div className="tw-px-4 tw-py-4">
                        <div className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{$t('Movement')}</div>
                        <div className="tw-mt-1 tw-text-base tw-font-bold tw-text-charcoal">Δ {$t('Variance')}</div>
                        <div className="tw-mt-0.5 tw-text-2xs tw-text-charcoal-50">{$t('value & %')}</div>
                    </div>
                </div>

                <ComparisonSection title={$t('Financial headlines')} rows={financial} formatValue={formatMoneyFigure} />
                <ComparisonSection title={$t('Shape of budget')} rows={shape} formatValue={formatCount} />
            </div>

            <div>
                <button type="button" className="tw-text-sm tw-font-medium tw-text-blue" onClick={onBack}>
                    {$t('← Back to versions')}
                </button>
            </div>
        </div>
    );
}
